import Vue from "vue";
import Vuex from "vuex";
import VuexPersist from "vuex-persist";
import api from "@/services";
import { connectSocket, disconnectSocket } from "@/socket";
import { upload } from "./modules/upload";
import { download } from "./modules/download";

Vue.use(Vuex);

const vuexLocal = new VuexPersist({
  key: "admin-tj-pb",
  storage: window.localStorage,
  modules: ["accessToken", "refreshToken", "user", "filters", "attendance"],
});

export default new Vuex.Store({
  state: {
    accessToken: "",
    refreshToken: "",
    deviceToken: "",
    user: null,
    attendance: null,
    audience: null,
    filters: {
      groups: [],
      subgroups: [],
      desks: [],
      attendants: [],
    },
    socketSession: null, // Dados da sessão do socket
    linkedAccess: true,
    attendanceAccess: true,
    audienceAccess: true,
  },

  getters: {
    authenticated: ({ accessToken, refreshToken, user }) =>
      !!accessToken && !!refreshToken && !!user,
    accessToken: ({ accessToken }) => accessToken,
    refreshToken: ({ refreshToken }) => refreshToken,
    deviceToken: ({ deviceToken }) => deviceToken,
    user: ({ user }) => user,
    attendance: ({ attendance }) => attendance,
    audience: ({ audience }) => audience,
    attendanceRole: ({ user }) => {
      // operator (attendant), moderator (coordinator), admin (director)

      if (!user?.permissionGroups) return null;

      const result = user.permissionGroups.find(
        (group) => !!group.role && group.module === "attendances"
      );
      return result ? result.role : null;
    },
    audienceRole: ({ user }) => {
      if (!user?.permissionGroups) return null;

      const result = user.permissionGroups.find(
        (group) => !!group.role && group.module === "audiences"
      );
      return result ? result.role : null;
    },
    masterPermission: ({ user }) => {
      if (!user?.permissionGroups?.length) return null;

      return !!user.permissionGroups.find((el) => el.name === "Master");
    },
    linkedAccess: ({ linkedAccess }) => linkedAccess,
    attendanceAccess: ({ attendanceAccess }) => attendanceAccess,
    audienceAccess: ({ audienceAccess }) => audienceAccess,
  },

  mutations: {
    SET_ACCESS_TOKEN(state, payload) {
      state.accessToken = payload;
    },
    SET_DEVICE_TOKEN(state, payload) {
      state.deviceToken = payload;
    },
    SET_REFRESH_TOKEN(state, payload) {
      state.refreshToken = payload;
    },
    SET_ACCOUNT(state, payload) {
      state.user = payload;
    },
    SET_FILTERS_GROUPS(state, payload) {
      state.filters.groups = payload;
    },
    SET_FILTERS_SUBGROUPS(state, payload) {
      state.filters.subgroups = payload;
    },
    SET_FILTERS_DESKS(state, payload) {
      state.filters.desks = payload;
    },
    SET_FILTERS_ATTENDANTS(state, payload) {
      state.filters.attendants = payload;
    },
    SET_ATTENDANCE(state, payload) {
      state.attendance = payload;
    },
    SET_AUDIENCE(state, payload) {
      state.audience = payload;
    },
    SET_USER_STATUS(state, payload) {
      if (!state.user) return;

      state.user.status = payload.status;
    },
    SET_SOCKET_SESSION(state, data) {
      state.socketSession = data;
    },
    SET_LINKED_ACCESS(state, payload) {
      state.linkedAccess = payload;
    },
    SET_ATTENDANCE_ACCESS(state, payload) {
      state.attendanceAccess = payload;
    },

    SET_AUDIENCE_ACCESS(state, payload) {
      state.audienceAccess = payload;
    },
  },

  actions: {
    setAccessToken({ commit }, payload) {
      commit("SET_ACCESS_TOKEN", payload);
      api.defaults.headers.common["Authorization"] = payload;
    },
    setRefreshToken({ commit }, payload) {
      commit("SET_REFRESH_TOKEN", payload);
    },
    setUserStatus({ commit }, payload) {
      commit("SET_USER_STATUS", payload);
    },
    setDeviceToken({ commit }, payload) {
      commit("SET_DEVICE_TOKEN", payload);
    },
    setUser({ commit }, payload) {
      commit("SET_ACCOUNT", payload);
    },
    setSignIn({ dispatch }, payload) {
      dispatch("setAccessToken", payload.accessToken);
      dispatch("setRefreshToken", payload.refreshToken);
      dispatch("setDeviceToken", payload.deviceToken);
      dispatch("setUser", payload.user);

      connectSocket();
    },
    setSignOut({ dispatch }) {
      dispatch("setAccessToken", "");
      dispatch("setRefreshToken", "");
      dispatch("setUser", null);
      dispatch("clearFilters");
      dispatch("setLinkedAccess", true);

      disconnectSocket();
    },
    setFiltersGroups({ commit }, payload) {
      commit("SET_FILTERS_GROUPS", payload);
    },
    setFiltersSubgroups({ commit }, payload) {
      commit("SET_FILTERS_SUBGROUPS", payload);
    },
    setFiltersDesks({ commit }, payload) {
      commit("SET_FILTERS_DESKS", payload);
    },
    setFiltersAttendants({ commit }, payload) {
      commit("SET_FILTERS_ATTENDANTS", payload);
    },
    setAttendance({ commit }, payload) {
      commit("SET_ATTENDANCE", payload);
    },
    setAudience({ commit }, payload) {
      commit("SET_AUDIENCE", payload);
    },
    clearFilters({ dispatch }) {
      dispatch("setFiltersGroups", []);
      dispatch("setFiltersSubgroups", []);
      dispatch("setFiltersDesks", []);
      dispatch("setFiltersAttendants", []);
    },
    setSocketSession({ commit }, payload) {
      commit("SET_SOCKET_SESSION", payload);
    },
    setLinkedAccess({ commit }, payload) {
      commit("SET_LINKED_ACCESS", payload);
    },
    setAttendanceAccess({ commit }, payload) {
      commit("SET_ATTENDANCE_ACCESS", payload);
    },
    setAudienceAccess({ commit }, payload) {
      commit("SET_AUDIENCE_ACCESS", payload);
    },
  },

  plugins: [vuexLocal.plugin],

  modules: {
    upload,
    download,
  },
});
